"use client";

import { useEffect } from "react";
import { trackFromDataset } from "./track";

/** `data-analytics-<veld>` uit een element halen als payload. */
function leesPayload(node: HTMLElement) {
  const { analyticsEvent, analyticsWhen, analyticsOnce, ...rest } = node.dataset;
  const payload: Record<string, string> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (!key.startsWith("analytics") || value === undefined) continue;
    const field = key.slice("analytics".length);
    payload[field.charAt(0).toLowerCase() + field.slice(1)] = value;
  }
  return { analyticsEvent, analyticsWhen, analyticsOnce, payload };
}

/**
 * Eén listener voor de hele site. Een element telt mee zodra het
 * `data-analytics-event` draagt; extra `data-analytics-<veld>`-attributen komen
 * als metadata mee.
 *
 *   <Link href="/start" data-analytics-event="hero_cta_clicked"
 *         data-analytics-component="hero" data-analytics-cta-label="Leg je plan voor">
 *
 * Standaard meet hij een klik. Met `data-analytics-when="visible"` meet hij in
 * plaats daarvan een weergave: het event vuurt zodra het element half in beeld
 * staat. `data-analytics-once="<sleutel>"` maakt daar één per sessie van, zodat
 * dezelfde pakketkaart op twee pagina's niet dubbel telt.
 *
 * Zo blijven serverpagina's serverpagina's: markup krijgt een attribuut, geen
 * eigen clientboundary of onClick-handler.
 */
export function AnalyticsListener() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const target = event.target as Element | null;
      const node = target?.closest?.("[data-analytics-event]");
      if (!(node instanceof HTMLElement)) return;

      const { analyticsEvent, analyticsWhen, payload } = leesPayload(node);
      if (!analyticsEvent || analyticsWhen === "visible") return;

      trackFromDataset(analyticsEvent, payload);
    }

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  // Weergave-events. Geen IntersectionObserver betekent niet meten: een event
  // dat niets over zichtbaarheid zegt is erger dan geen event.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const gezien = (sleutel: string | undefined) => {
      if (!sleutel) return false;
      try {
        return window.sessionStorage.getItem(`evilla:analytics:gezien:${sleutel}`) !== null;
      } catch {
        // Privémodus: dan meten we één keer per paginabezoek.
        return false;
      }
    };

    const waarnemer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;
          waarnemer.unobserve(entry.target);

          const { analyticsEvent, analyticsOnce, payload } = leesPayload(entry.target);
          if (!analyticsEvent || gezien(analyticsOnce)) continue;
          if (analyticsOnce) {
            try {
              window.sessionStorage.setItem(`evilla:analytics:gezien:${analyticsOnce}`, "1");
            } catch {
              // Zie hierboven.
            }
          }

          trackFromDataset(analyticsEvent, payload);
        }
      },
      { threshold: 0.5 },
    );

    for (const node of document.querySelectorAll('[data-analytics-when="visible"]')) {
      waarnemer.observe(node);
    }

    return () => waarnemer.disconnect();
  }, []);

  return null;
}
